// GENERATED by architecture/tools/gen_kinds_ts.py — do not edit.
//
// The kind catalog, from `kinds.py`. Gap #5 asked for one vocabulary; this is
// the browser's view of the same table `canonical.py` emits from, so a kind
// cannot exist on one side of the wire and not the other.
//
// `LABEL` is the Romanian display string. It is what `display.label` already
// carries per entity — this map is the fallback for an entity whose label is
// missing, and the source for UI chrome that names a kind with no entity in
// hand (a legend, a filter, an empty state).

/** One plot edge, and what it is next to. */
export type EdgeKind =
  | "neighbour"
  | "open"
  | "street";

/** Structure and circulation. */
export type ElementKind =
  | "ada_aisle"
  | "axis"
  | "cantilever"
  | "cat_ladder"
  | "column"
  | "floor"
  | "foundation"
  | "frame"
  | "lane"
  | "lintel"
  | "offset"
  | "on_grade"
  | "pad"
  | "platform"
  | "ramp"
  | "ramp_access"
  | "slab_panel"
  | "steps"
  | "strip"
  | "tie"
  | "transfer";

/** facade kinds. */
export type FacadeKind =
  | "band"
  | "brick"
  | "cladding"
  | "curtain_wall"
  | "flooring"
  | "glass"
  | "glazing"
  | "insulation"
  | "planter"
  | "plaster"
  | "render"
  | "screen"
  | "sill"
  | "slats"
  | "solid"
  | "subframe";

/** The land, and the constraints that shape it. */
export type LandKind =
  | "basement"
  | "buildable"
  | "courtyard"
  | "diggable"
  | "easement"
  | "playground"
  | "square"
  | "urban_zone"
  | "waste_platform";

/** Openings. */
export type OpeningKind =
  | "door"
  | "fire_door"
  | "hatch"
  | "shelter_door"
  | "window";

/** The on-grade layer: roads, pavements, green, entrances, trees. */
export type SiteKind =
  | "access"
  | "aisle"
  | "apron"
  | "crossing"
  | "entrance"
  | "fire_spur"
  | "green_deep"
  | "green_slab"
  | "loop"
  | "path"
  | "road"
  | "sidewalk"
  | "site"
  | "spine"
  | "tree"
  | "turning_head";

/** Everything that is a room-ish space: garage programme and flat rooms. */
export type SpaceLikeKind =
  | "administrativ"
  | "ala"
  | "ala_gs"
  | "ala_iesire"
  | "ala_sas"
  | "balcony"
  | "bath"
  | "bath2"
  | "bazin"
  | "bedroom"
  | "birouri"
  | "boxe"
  | "casa_scarii_evacuare"
  | "comercial"
  | "coridor_evacuare"
  | "desfumare"
  | "dressing"
  | "ecs"
  | "fdcp"
  | "garage"
  | "ghena"
  | "gospodarie"
  | "hall"
  | "hol"
  | "intrare_acoperita"
  | "invatamant"
  | "kitchen"
  | "lift"
  | "living"
  | "office"
  | "pantry"
  | "room"
  | "sas"
  | "scara"
  | "shower"
  | "store"
  | "te"
  | "te_desfumare"
  | "tech"
  | "terasa"
  | "terasa_circulabila"
  | "wc";

/** Parking bay kinds. */
export type StallKind =
  | "ada"
  | "ev"
  | "std";

/** Wall kinds. `party` is the structural one. */
export type WallKind =
  | "ala_perete"
  | "facade"
  | "fire"
  | "parapet"
  | "partition"
  | "party"
  | "structural";

/** Groupings: a flat is a zone of rooms, not a room. */
export type ZoneKind =
  | "bar"
  | "building"
  | "core"
  | "flat"
  | "tronson";

/** Storey kinds — the vocabulary of `storey_kind`, which is
 *  NOT the vocabulary of `kind`. `basement` deliberately
 *  appears here and in the land kinds with two meanings: the
 *  fields differ, so a reader always knows which it has. */
export type StoreyKind =
  | "apartment"
  | "basement_storey"
  | "ground"
  | "parking"
  | "roof";

/** Romanian display string per storey kind. */
export const STOREY_LABEL: Record<StoreyKind, string> = {
  apartment: "Etaj curent",
  basement_storey: "Subsol",
  ground: "Parter",
  parking: "Subsol parcare",
  roof: "Terasă",
};

/** Object types: id prefix, and the kinds each may carry.
 *  `kinds: null` means the type carries no `kind` at all — a
 *  Flat is a Flat, and repeating that as `kind: "flat"` would be
 *  the same fact twice. */
export const TYPE: Record<string, { prefix: string; kinds: readonly string[] | null }> = {
  AccessibleAisle: { prefix: "aa", kinds: null },
  Area: { prefix: "ar", kinds: ["buildable", "diggable", "basement", "courtyard", "easement", "playground", "waste_platform", "square", "urban_zone"] },
  Axis: { prefix: "ax", kinds: null },
  Bar: { prefix: "br", kinds: null },
  Beam: { prefix: "bm", kinds: ["frame", "transfer", "lintel", "tie", "foundation"] },
  Building: { prefix: "bd", kinds: null },
  Column: { prefix: "cl", kinds: null },
  Core: { prefix: "co", kinds: null },
  CorePart: { prefix: "cp", kinds: ["scara", "lift", "sas", "coridor_evacuare", "casa_scarii_evacuare", "ghena", "hol", "administrativ", "comercial", "birouri", "invatamant", "intrare_acoperita", "bazin", "boxe", "fdcp", "te", "ecs", "te_desfumare", "gospodarie", "desfumare"] },
  Door: { prefix: "dr", kinds: ["door", "fire_door", "shelter_door"] },
  Entrance: { prefix: "en", kinds: ["entrance", "access"] },
  Facade: { prefix: "fa", kinds: null },
  FacadePanel: { prefix: "fp", kinds: ["insulation", "render", "subframe", "plaster", "cladding", "brick", "glazing", "curtain_wall", "band", "sill", "flooring"] },
  Flat: { prefix: "fl", kinds: null },
  Footing: { prefix: "ft", kinds: ["strip", "pad"] },
  Furniture: { prefix: "fn", kinds: null },
  GreenArea: { prefix: "gn", kinds: ["green_deep", "green_slab"] },
  Ladder: { prefix: "ld", kinds: ["cat_ladder"] },
  Lane: { prefix: "ln", kinds: null },
  Lift: { prefix: "lf", kinds: ["platform"] },
  ParkingSpace: { prefix: "sp", kinds: ["std", "ada", "ev"] },
  PlotEdge: { prefix: "pe", kinds: ["street", "neighbour", "open"] },
  Railing: { prefix: "rl", kinds: ["glass", "slats", "solid", "planter", "screen"] },
  Ramp: { prefix: "rp", kinds: ["ramp", "ramp_access"] },
  Road: { prefix: "rd", kinds: ["road", "aisle", "spine", "loop", "turning_head", "fire_spur"] },
  Sidewalk: { prefix: "sw", kinds: ["sidewalk", "apron", "path", "crossing"] },
  Site: { prefix: "si", kinds: ["site"] },
  Slab: { prefix: "sl", kinds: ["floor", "cantilever", "on_grade", "offset"] },
  SlabPanel: { prefix: "pn", kinds: null },
  Space: { prefix: "rm", kinds: ["scara", "lift", "sas", "coridor_evacuare", "casa_scarii_evacuare", "ghena", "hol", "administrativ", "comercial", "birouri", "invatamant", "intrare_acoperita", "bazin", "boxe", "fdcp", "te", "ecs", "te_desfumare", "gospodarie", "desfumare", "living", "bedroom", "kitchen", "bath", "bath2", "wc", "shower", "store", "hall", "office", "dressing", "pantry", "balcony", "terasa", "terasa_circulabila", "tech", "garage", "ala", "ala_sas", "ala_gs", "ala_iesire", "room"] },
  Stair: { prefix: "sc", kinds: ["steps"] },
  Storey: { prefix: "st", kinds: null },
  Tree: { prefix: "tr", kinds: null },
  Tronson: { prefix: "tn", kinds: null },
  Wall: { prefix: "wl", kinds: ["structural", "party", "partition", "facade", "fire", "parapet", "ala_perete"] },
  Window: { prefix: "wn", kinds: ["window", "hatch"] },
};

export type ObjectType = keyof typeof TYPE;

/** Every kind in the catalog. */
export type Kind =
  | EdgeKind
  | ElementKind
  | FacadeKind
  | LandKind
  | OpeningKind
  | SiteKind
  | SpaceLikeKind
  | StallKind
  | WallKind
  | ZoneKind;

/** Romanian display string per kind. */
export const LABEL: Record<string, string> = {
  access: "Acces pe limita terenului",
  ada: "Loc de parcare adaptat",
  ada_aisle: "Spațiu de transfer ADA",
  administrativ: "Spațiu administrativ",
  aisle: "Alee carosabilă",
  ala: "Adăpost ALA",
  ala_gs: "Grup sanitar adăpost",
  ala_iesire: "Ieșire de salvare adăpost",
  ala_perete: "Perete adăpost ALA",
  ala_sas: "SAS adăpost",
  apron: "Platformă de acces",
  axis: "Ax structural",
  balcony: "Balcon",
  band: "Brâu în relief",
  bar: "Segment de bară",
  basement: "Parcaj subteran",
  bath: "Baie",
  bath2: "Baie 2",
  bazin: "Bazin retenție",
  bedroom: "Dormitor",
  birouri: "Birouri",
  boxe: "Boxe",
  brick: "Cărămidă aparentă",
  buildable: "Zonă edificabilă",
  building: "Corp",
  cantilever: "Placă în consolă",
  casa_scarii_evacuare: "Casă de scară de evacuare",
  cat_ladder: "Scară de pisică",
  cladding: "Placaj",
  column: "Stâlp / lamelă",
  comercial: "Spațiu comercial",
  core: "Nucleu",
  coridor_evacuare: "Coridor de evacuare",
  courtyard: "Curte interioară",
  crossing: "Trecere de pietoni",
  curtain_wall: "Perete cortină",
  desfumare: "Zonă de desfumare",
  diggable: "Zonă excavabilă",
  door: "Ușă",
  dressing: "Dressing",
  easement: "Servitute / zonă de protecție",
  ecs: "ECS",
  entrance: "Acces clădire",
  ev: "Loc de parcare electric",
  facade: "Perete de fațadă",
  fdcp: "FDCP",
  fire: "Perete antifoc",
  fire_door: "Ușă rezistentă la foc",
  fire_spur: "Acces pompieri",
  flat: "Apartament",
  floor: "Placă de planșeu",
  flooring: "Pardoseală exterioară",
  foundation: "Grindă de fundare",
  frame: "Grindă de cadru",
  garage: "Garaj",
  ghena: "Ghenă / tablou",
  glass: "Balustradă sticlă",
  glazing: "Vitraj (perete-cortină)",
  gospodarie: "Gospodărie apă",
  green_deep: "Spațiu verde pe sol",
  green_slab: "Spațiu verde pe placă",
  hall: "Hol",
  hatch: "Oblon metalic etanș",
  hol: "Hol distribuție",
  insulation: "Termoizolație",
  intrare_acoperita: "Intrare acoperită",
  invatamant: "Învățământ",
  kitchen: "Bucătărie",
  lane: "Alee de circulație",
  lift: "Ascensor",
  lintel: "Buiandrug",
  living: "Living",
  loop: "Inel carosabil",
  neighbour: "Latură la vecin",
  office: "Birou",
  offset: "Placă la altă cotă",
  on_grade: "Placă pe sol",
  open: "Latură liberă",
  pad: "Fundație izolată",
  pantry: "Cămară",
  parapet: "Parapet",
  partition: "Compartimentare",
  party: "Perete despărțitor",
  path: "Alee pietonală",
  planter: "Jardinieră",
  plaster: "Tencuială decorativă",
  platform: "Platformă elevatoare",
  playground: "Loc de joacă",
  ramp: "Rampă",
  ramp_access: "Rampă acces parcaj subteran",
  render: "Tencuială de bază",
  road: "Drum",
  room: "Cameră",
  sas: "SAS",
  scara: "Casă de scară",
  screen: "Ecran fix",
  shelter_door: "Ușă metalică etanșă de protecție",
  shower: "Duș",
  sidewalk: "Trotuar",
  sill: "Glaf",
  site: "Plan de situație",
  slab_panel: "Ochi de placă",
  slats: "Balustradă lamele",
  solid: "Balustradă plină",
  spine: "Drum principal",
  square: "Piațetă",
  std: "Loc de parcare",
  steps: "Trepte",
  store: "Debara",
  street: "Latură la stradă",
  strip: "Fundație continuă",
  structural: "Perete structural",
  subframe: "Substructură fațadă ventilată",
  te: "TE",
  te_desfumare: "TE desfumare",
  tech: "Spațiu tehnic",
  terasa: "Terasă",
  terasa_circulabila: "Terasă circulabilă",
  tie: "Grindă de legătură",
  transfer: "Grindă de transfer",
  tree: "Arbore",
  tronson: "Tronson",
  turning_head: "Platformă de întoarcere",
  urban_zone: "Zonă de regim urbanistic",
  waste_platform: "Platformă de gunoi",
  wc: "WC serviciu",
  window: "Fereastră",
};

/** Classification hook (gap #8): [IfcClass, PredefinedType]. */
export const IFC: Record<string, [string, string | null]> = {
  access: ["IfcCivilElement", null],
  ada: ["IfcSpace", "PARKING"],
  ada_aisle: ["IfcSpace", "INTERNAL"],
  administrativ: ["IfcSpace", "INTERNAL"],
  aisle: ["IfcCivilElement", "ROAD"],
  ala: ["IfcSpace", "INTERNAL"],
  ala_gs: ["IfcSpace", "INTERNAL"],
  ala_iesire: ["IfcSpace", "INTERNAL"],
  ala_perete: ["IfcWall", "SOLIDWALL"],
  ala_sas: ["IfcSpace", "INTERNAL"],
  apron: ["IfcCivilElement", "PAVEMENT"],
  axis: ["IfcGridAxis", null],
  balcony: ["IfcSpace", "EXTERNAL"],
  band: ["IfcCovering", "MOLDING"],
  bar: ["IfcSpatialZone", null],
  basement: ["IfcSpatialZone", null],
  bath: ["IfcSpace", "INTERNAL"],
  bath2: ["IfcSpace", "INTERNAL"],
  bazin: ["IfcSpace", "INTERNAL"],
  bedroom: ["IfcSpace", "INTERNAL"],
  birouri: ["IfcSpace", "INTERNAL"],
  boxe: ["IfcSpace", "INTERNAL"],
  brick: ["IfcCovering", "CLADDING"],
  buildable: ["IfcSpatialZone", null],
  building: ["IfcBuilding", null],
  cantilever: ["IfcSlab", "FLOOR"],
  casa_scarii_evacuare: ["IfcSpace", "INTERNAL"],
  cat_ladder: ["IfcStair", "USERDEFINED"],
  cladding: ["IfcCovering", "CLADDING"],
  column: ["IfcColumn", null],
  comercial: ["IfcSpace", "INTERNAL"],
  core: ["IfcZone", null],
  coridor_evacuare: ["IfcSpace", "INTERNAL"],
  courtyard: ["IfcExternalSpatialElement", null],
  crossing: ["IfcCivilElement", "PAVEMENT"],
  curtain_wall: ["IfcCurtainWall", null],
  desfumare: ["IfcSpace", "INTERNAL"],
  diggable: ["IfcSpatialZone", null],
  door: ["IfcDoor", null],
  dressing: ["IfcSpace", "INTERNAL"],
  easement: ["IfcSpatialZone", null],
  ecs: ["IfcSpace", "INTERNAL"],
  entrance: ["IfcDoor", null],
  ev: ["IfcSpace", "PARKING"],
  facade: ["IfcWall", "SOLIDWALL"],
  fdcp: ["IfcSpace", "INTERNAL"],
  fire: ["IfcWall", "SOLIDWALL"],
  fire_door: ["IfcDoor", null],
  fire_spur: ["IfcCivilElement", "ROAD"],
  flat: ["IfcZone", null],
  floor: ["IfcSlab", "FLOOR"],
  flooring: ["IfcCovering", "FLOORING"],
  foundation: ["IfcBeam", "BEAM"],
  frame: ["IfcBeam", "BEAM"],
  garage: ["IfcSpace", "INTERNAL"],
  ghena: ["IfcSpace", "INTERNAL"],
  glass: ["IfcRailing", "GUARDRAIL"],
  glazing: ["IfcCurtainWall", null],
  gospodarie: ["IfcSpace", "INTERNAL"],
  green_deep: ["IfcGeographicElement", null],
  green_slab: ["IfcGeographicElement", null],
  hall: ["IfcSpace", "INTERNAL"],
  hatch: ["IfcDoor", "TRAPDOOR"],
  hol: ["IfcSpace", "INTERNAL"],
  insulation: ["IfcCovering", "INSULATION"],
  intrare_acoperita: ["IfcSpace", "EXTERNAL"],
  invatamant: ["IfcSpace", "INTERNAL"],
  kitchen: ["IfcSpace", "INTERNAL"],
  lane: ["IfcSpace", "INTERNAL"],
  lift: ["IfcSpace", "INTERNAL"],
  lintel: ["IfcBeam", "LINTEL"],
  living: ["IfcSpace", "INTERNAL"],
  loop: ["IfcCivilElement", "ROAD"],
  neighbour: ["IfcAnnotation", null],
  office: ["IfcSpace", "INTERNAL"],
  offset: ["IfcSlab", "FLOOR"],
  on_grade: ["IfcSlab", "BASESLAB"],
  open: ["IfcAnnotation", null],
  pad: ["IfcFooting", "PAD_FOOTING"],
  pantry: ["IfcSpace", "INTERNAL"],
  parapet: ["IfcWall", "PARAPET"],
  partition: ["IfcWall", "PARTITIONING"],
  party: ["IfcWall", "SOLIDWALL"],
  path: ["IfcCivilElement", "PAVEMENT"],
  planter: ["IfcRailing", "GUARDRAIL"],
  plaster: ["IfcCovering", "CLADDING"],
  platform: ["IfcTransportElement", "LIFTINGGEAR"],
  playground: ["IfcSpatialZone", null],
  ramp: ["IfcRamp", null],
  ramp_access: ["IfcRamp", null],
  render: ["IfcCovering", "CLADDING"],
  road: ["IfcCivilElement", "ROAD"],
  room: ["IfcSpace", "INTERNAL"],
  sas: ["IfcSpace", "INTERNAL"],
  scara: ["IfcSpace", "INTERNAL"],
  screen: ["IfcRailing", "GUARDRAIL"],
  shelter_door: ["IfcDoor", null],
  shower: ["IfcSpace", "INTERNAL"],
  sidewalk: ["IfcCivilElement", "PAVEMENT"],
  sill: ["IfcCovering", "MOLDING"],
  site: ["IfcSite", null],
  slab_panel: ["IfcSpatialZone", null],
  slats: ["IfcRailing", "GUARDRAIL"],
  solid: ["IfcRailing", "GUARDRAIL"],
  spine: ["IfcCivilElement", "ROAD"],
  square: ["IfcSpatialZone", null],
  std: ["IfcSpace", "PARKING"],
  steps: ["IfcStairFlight", "STRAIGHT"],
  store: ["IfcSpace", "INTERNAL"],
  street: ["IfcAnnotation", null],
  strip: ["IfcFooting", "STRIP_FOOTING"],
  structural: ["IfcWall", "SOLIDWALL"],
  subframe: ["IfcMember", "MEMBER"],
  te: ["IfcSpace", "INTERNAL"],
  te_desfumare: ["IfcSpace", "INTERNAL"],
  tech: ["IfcSpace", "INTERNAL"],
  terasa: ["IfcSpace", "EXTERNAL"],
  terasa_circulabila: ["IfcSpace", "EXTERNAL"],
  tie: ["IfcBeam", "BEAM"],
  transfer: ["IfcBeam", "BEAM"],
  tree: ["IfcGeographicElement", null],
  tronson: ["IfcSpatialZone", null],
  turning_head: ["IfcCivilElement", "ROAD"],
  urban_zone: ["IfcSpatialZone", null],
  waste_platform: ["IfcSpatialZone", null],
  wc: ["IfcSpace", "INTERNAL"],
  window: ["IfcWindow", null],
};

/** What a door leaf is for. */
export type DoorRole = "entry" | "interior";

/** Habitable rooms must reach a facade (CZ-R27/DR-R27). */
export const HABITABLE: readonly string[] = ["living", "bedroom"];

/** Sanitary rooms, as flat.py groups them. */
export const WET: readonly string[] = ["bath", "bath2", "wc", "shower"];
